/**
 * Tenant branding — who this installation belongs to, fetched before sign-in.
 *
 * The same endpoint the mobile app calls at startup, asked by hostname instead
 * of by package name. That is deliberate: one resolution chain means the app
 * and the browser can never disagree about what an institute is called.
 *
 *   GET /interlearn/appbuilder/branding/?host=greenwood.eduexpertai.com
 *
 * A 404 is the NORMAL answer on the platform's own domain and must be treated
 * as "use the platform defaults", never as an error. Every field is optional
 * for the same reason: the server drops empty values rather than sending "",
 * so a tenant that has set only a logo does not blank the rest.
 */

import axios from 'axios';

import { getValidToken } from './tokenService';
import { OPEN_ACCESS, TenantAccess } from './tenantRoutes';

/**
 * The branding endpoint's `access` block. A missing block means a backend from
 * before access control, which never blocked anyone, so it reads as open. A
 * PRESENT block is taken strictly: only an explicit `true` opens a switch.
 */
const parseAccess = (raw: unknown): TenantAccess => {
  if (!raw || typeof raw !== 'object') return OPEN_ACCESS;
  const a = raw as Record<string, unknown>;
  return {
    state: typeof a.state === 'string' ? a.state : 'unknown',
    publicSite: a.public_site === true,
    portal: a.portal === true,
    message: typeof a.message === 'string' ? a.message : '',
  };
};

export interface TenantBranding {
  appName?: string;
  shortName?: string;
  tagline?: string;
  legalName?: string;
  address?: string;
  supportEmail?: string;
  phone?: string;
  website?: string;
  logoUrl?: string;
  /** Hex, e.g. "#2563EB". Converted to an HSL triplet before it reaches CSS. */
  primaryColor?: string;
  faviconUrl?: string;
  loginBgUrl?: string;
}

export interface TenantContext {
  instituteId: number | null;
  landingMode: 'platform_home' | 'tenant_home';
  homePath: string;
  /** The institute's own marketing site, if they have one. '' when not. */
  landingUrl: string;
  /** What master currently allows (Access control). */
  access: TenantAccess;
  branding: TenantBranding;
  /** True when no tenant owns this address — the platform's own domain. */
  isPlatform: boolean;
}

/**
 * What every surface shows when the tenant has not supplied its own.
 *
 * Defined once, here, because the fallback is a guarantee rather than a
 * convenience: the sign-in screen and the app shell must ALWAYS render a name
 * and a mark, whether the lookup 404s on the platform domain, the API is
 * unreachable, a tenant filled in only a colour, or an image URL is broken.
 * Scattered `?? "EduExpertAI"` literals were four chances to miss one.
 *
 * The logo is a bundled path, never a remote URL — a default that can itself
 * fail to load is not a default.
 */
export const PLATFORM_BRAND = {
  name: 'EduExpertAI',
  logoUrl: '/logo.png',
} as const;

export const PLATFORM_CONTEXT: TenantContext = {
  instituteId: null,
  landingMode: 'platform_home',
  homePath: '',
  landingUrl: '',
  access: OPEN_ACCESS,
  branding: {},
  isPlatform: true,
};

/**
 * The brand colour, outside React.
 *
 * ThemeContext owns `--primary` and rewrites it whenever the user changes a
 * theme setting. If the branding provider also wrote that variable the two
 * would overwrite each other depending on which effect ran last, which is not
 * a race worth having. Instead ThemeContext reads this, so there is still
 * exactly one writer.
 */
let brandPrimaryHsl: string | null = null;

/**
 * The colour of the institute open in the dashboard (useInstituteBrandColour).
 * Outranks the host's tenant colour: a super admin on the platform domain
 * looking at an institute sees that institute's colour, and so does its staff.
 */
let institutePrimaryHsl: string | null = null;

const brandListeners = new Set<() => void>();
const notifyBrand = () => brandListeners.forEach((fn) => fn());

/** ThemeContext repaints when either brand colour changes. Returns unsubscribe. */
export const subscribeBrandPrimary = (fn: () => void): (() => void) => {
  brandListeners.add(fn);
  return () => {
    brandListeners.delete(fn);
  };
};

/** The tenant's primary colour as an HSL triplet, or null for the platform's. */
export const getBrandPrimaryHsl = (): string | null => institutePrimaryHsl || brandPrimaryHsl;

export const setBrandPrimaryHsl = (value: string | null): void => {
  if (value === brandPrimaryHsl) return;
  brandPrimaryHsl = value;
  notifyBrand();
};

export const setInstitutePrimaryHsl = (value: string | null): void => {
  if (value === institutePrimaryHsl) return;
  institutePrimaryHsl = value;
  notifyBrand();
};

/**
 * "#2563EB" as "220 87% 55%" — the shape ThemeContext writes into `--primary`.
 *
 * Returns null for anything unparseable so a mistyped colour in the admin form
 * falls back to the platform palette instead of producing an invalid custom
 * property, which CSS ignores silently and which would leave the app with no
 * primary colour at all.
 */
export function hexToHslTriplet(hex?: string): string | null {
  if (!hex) return null;
  let value = hex.trim().replace(/^#/, '');
  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;

  const r = parseInt(value.slice(0, 2), 16) / 255;
  const g = parseInt(value.slice(2, 4), 16) / 255;
  const b = parseInt(value.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Resolve the tenant for the address this page is being served from.
 *
 * The hostname is sent explicitly rather than left for the server to read off
 * its own Host header: the API and the app are separate hosts today, so the
 * server's idea of "where am I" is the API domain, not the browser's.
 *
 * Never throws. A failed lookup, an offline API and a 404 all mean the same
 * thing to the caller — render as the platform.
 *
 * Goes through axios rather than fetch, and not as a style preference: every
 * endpoint behind ConsolidatedMiddleware is rejected with 400 "Missing required
 * encryption headers" unless it carries X-Client-Key / X-Key-Id /
 * X-Key-Signature, and those are attached by the global request interceptor in
 * axios-global-autoclean. A bare fetch never reaches that interceptor, so this
 * one call — alone in the app — went out unsigned and always failed.
 */
export async function fetchTenantContext(
  baseURL: string,
  signal?: AbortSignal,
): Promise<TenantContext> {
  try {
    const host = window.location.hostname;
    // On the free path address (eduexpertai.com/institutes/<slug>/...) the
    // institute is named by the path, not the host: ask by slug so its own
    // login page and site wear its brand.
    const pathSlug = /^\/institutes\/([a-z0-9-]+)(?:\/|$)/i.exec(window.location.pathname)?.[1]?.toLowerCase();

    // Sent when there is a session, omitted when there is not. getValidToken()
    // resolves to null rather than throwing or redirecting when nobody is
    // signed in, which is what makes it safe here: the view is AllowAny and
    // must still answer on the login screen, before any token exists.
    const token = await getValidToken().catch(() => null);

    const response = await axios.get(
      `${baseURL}/interlearn/appbuilder/branding/`,
      {
        params: pathSlug ? { slug: pathSlug } : { host },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal,
        withCredentials: false,
        // 404 is the normal answer on the platform's own domain, so resolve it
        // instead of rejecting. That keeps an expected response out of the
        // global error interceptor, which logs every rejection and inspects it
        // for session/tenant failures that do not apply here.
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      },
    );
    if (response.status === 404) return PLATFORM_CONTEXT;

    const data = response.data;
    const branding: TenantBranding = data?.branding ?? {};

    return {
      instituteId: typeof data?.institute_id === 'number' ? data.institute_id : null,
      landingMode: data?.landing_mode === 'tenant_home' ? 'tenant_home' : 'platform_home',
      homePath: typeof data?.home_path === 'string' ? data.home_path : '',
      // Only an absolute http(s) URL is honoured: it is used as a redirect
      // target, and anything else (javascript:, a relative path) must not be.
      landingUrl:
        typeof data?.landing_url === 'string' && /^https?:\/\//i.test(data.landing_url)
          ? data.landing_url
          : '',
      access: parseAccess(data?.access),
      branding,
      isPlatform: false,
    };
  } catch {
    return PLATFORM_CONTEXT;
  }
}
